import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  Select,
} from '@sakhtar/design-system/react';
import { displayJalaliDate, displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import {
  createOperatorOrganization,
  overrideOperatorTrial,
  getOperatorOrganization,
  listOperatorPlans,
  listOperatorSmsDeliveries,
  refreshOperatorSmsDelivery,
  listOrganizationUsers,
  listOrganizationErrors,
  listOrganizationBillingOrders,
  changeOrganizationPlan,
  operatorSupportLogin,
  reactivateOperatorOrganization,
  suspendOperatorOrganization,
  type OrganizationDetail,
  type OperatorPlan,
  type OperatorSmsDelivery,
  type OperatorBillingOrder,
  type TenantUserSummary,
  type TenantIssueSummary,
} from '../../lib/operatorApi.js';
import { OperatorPage } from './OperatorShell.js';
import { BrandColorChip, OrgAvatar, OrgUserAvatar, WithAvatar } from './OperatorAvatars.js';

type PendingAction = 'suspend' | 'reactivate' | 'extend' | 'shorten' | 'cancel' | null;

const MIN_TRIAL_ADJUSTMENT_DAYS = 1;
const MAX_TRIAL_ADJUSTMENT_DAYS = 365;

const LIFECYCLE_CONFIG: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'default' }
> = {
  active: { label: 'فعال', tone: 'success' },
  provisioning: { label: 'در حال راه‌اندازی', tone: 'warning' },
  readonly: { label: 'فقط‌خواندنی', tone: 'default' },
  suspended: { label: 'معلق', tone: 'danger' },
  archived: { label: 'بایگانی‌شده', tone: 'default' },
  failed: { label: 'راه‌اندازی ناموفق', tone: 'danger' },
};

const COMMERCIAL_STATUS_CONFIG: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'default' }
> = {
  active: { label: 'فعال', tone: 'success' },
  expired: { label: 'منقضی‌شده', tone: 'danger' },
  suspended: { label: 'معلق', tone: 'danger' },
  canceled: { label: 'لغوشده', tone: 'default' },
};

const COMMERCIAL_MODE_CONFIG: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'default' }
> = {
  trial: { label: 'آزمایشی', tone: 'info' },
  paid: { label: 'پرداختی (تجاری)', tone: 'success' },
  free: { label: 'رایگان', tone: 'default' },
};

const SMS_DELIVERY_STATE_CONFIG: Record<
  number,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'default' }
> = {
  1: { label: 'تحویل به گیرنده', tone: 'success' },
  2: { label: 'تحویل‌نشده', tone: 'danger' },
  3: { label: 'در حال پردازش مخابرات', tone: 'info' },
  4: { label: 'به مخابرات نرسیده', tone: 'danger' },
  5: { label: 'تحویل به مخابرات', tone: 'warning' },
  6: { label: 'خطای تحویل', tone: 'danger' },
  7: { label: 'شماره در فهرست سیاه', tone: 'danger' },
};

function isValidTrialAdjustmentDays(value: string): boolean {
  const n = Number(value);
  return (
    value.trim() !== '' &&
    Number.isInteger(n) &&
    n >= MIN_TRIAL_ADJUSTMENT_DAYS &&
    n <= MAX_TRIAL_ADJUSTMENT_DAYS
  );
}

export function OrganizationDetailPage() {
  const { organizationRef } = useParams<{ organizationRef: string }>();
  const navigate = useNavigate();
  const [org, setOrg] = useState<OrganizationDetail | null>(null);
  const [smsDeliveries, setSmsDeliveries] = useState<readonly OperatorSmsDelivery[] | null>(null);
  const [billingOrders, setBillingOrders] = useState<readonly OperatorBillingOrder[] | null>(null);
  const [users, setUsers] = useState<readonly TenantUserSummary[] | null>(null);
  const [issues, setIssues] = useState<readonly TenantIssueSummary[] | null>(null);
  const [plans, setPlans] = useState<readonly OperatorPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('7');
  const [pending, setPending] = useState<PendingAction>(null);
  const [busy, setBusy] = useState(false);
  const [refreshingDeliveryRef, setRefreshingDeliveryRef] = useState<string | null>(null);
  const [smsRefreshError, setSmsRefreshError] = useState<string | null>(null);

  const [changePlanRef, setChangePlanRef] = useState('');
  const [changeSeatLimit, setChangeSeatLimit] = useState('');
  const [changePlanReason, setChangePlanReason] = useState('');
  const [changePlanBusy, setChangePlanBusy] = useState(false);
  const [changePlanSuccess, setChangePlanSuccess] = useState<string | null>(null);
  const [changePlanError, setChangePlanError] = useState<string | null>(null);

  const [supportLoginBusy, setSupportLoginBusy] = useState(false);
  const [supportLoginError, setSupportLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationRef) return;
    void getOperatorOrganization(organizationRef)
      .then(setOrg)
      .catch(() => setError('سازمان پیدا نشد.'));

    void listOperatorSmsDeliveries(organizationRef)
      .then(setSmsDeliveries)
      .catch(() => setSmsDeliveries([]));

    void listOrganizationBillingOrders(organizationRef)
      .then((res) => setBillingOrders(res.orders))
      .catch(() => setBillingOrders([]));

    void listOrganizationUsers(organizationRef)
      .then(setUsers)
      .catch(() => setUsers([]));

    void listOrganizationErrors(organizationRef)
      .then(setIssues)
      .catch(() => setIssues([]));

    void listOperatorPlans()
      .then(setPlans)
      .catch(() => setPlans([]));
  }, [organizationRef]);

  if (!organizationRef) return null;
  if (org === null && error === null) return <LoadingState />;

  async function handleSmsDeliveryRefresh(deliveryRef: string) {
    if (!organizationRef) return;
    setRefreshingDeliveryRef(deliveryRef);
    setSmsRefreshError(null);
    try {
      const refreshed = await refreshOperatorSmsDelivery(organizationRef, deliveryRef);
      setSmsDeliveries(
        (current) =>
          current?.map((row) => (row.deliveryRef === deliveryRef ? refreshed : row)) ?? [refreshed],
      );
    } catch {
      setSmsRefreshError('به‌روزرسانی وضعیت تحویل ناموفق بود؛ هیچ پیامک جدیدی ارسال نشد.');
    } finally {
      setRefreshingDeliveryRef(null);
    }
  }

  async function handleSupportLogin() {
    if (!org) return;
    setSupportLoginBusy(true);
    setSupportLoginError(null);
    try {
      const res = await operatorSupportLogin(org.slug);
      if (res.redirectTo) {
        window.open(res.redirectTo, '_blank');
      }
    } catch {
      setSupportLoginError('ورود پشتیبان به پنل تننت ناموفق بود.');
    } finally {
      setSupportLoginBusy(false);
    }
  }

  async function handleChangePlan(e: FormEvent) {
    e.preventDefault();
    if (!org || !changePlanRef || !changePlanReason.trim()) return;
    setChangePlanBusy(true);
    setChangePlanSuccess(null);
    setChangePlanError(null);
    try {
      const seatNum = changeSeatLimit.trim() ? Number(changeSeatLimit) : undefined;
      const updated = await changeOrganizationPlan(
        org.slug,
        changePlanRef,
        changePlanReason,
        seatNum,
      );
      setOrg(updated);
      setChangePlanSuccess(
        `طرح سازمان با موفقیت به «${updated.commercial.planDisplayName}» تغییر یافت.`,
      );
      setChangePlanReason('');
      setChangeSeatLimit('');
    } catch {
      setChangePlanError('تغییر طرح سازمان ناموفق بود.');
    } finally {
      setChangePlanBusy(false);
    }
  }

  async function run(action: Exclude<PendingAction, null>) {
    if (!org) return;
    setBusy(true);
    setError(null);
    try {
      const next =
        action === 'suspend'
          ? await suspendOperatorOrganization(org.slug, reason)
          : action === 'reactivate'
            ? await reactivateOperatorOrganization(org.slug, reason)
            : await overrideOperatorTrial(
                org.slug,
                action,
                reason,
                action === 'cancel' ? undefined : Number(days),
              );
      setOrg(next);
      setReason('');
    } catch {
      setError('انجام عملیات ناموفق بود.');
    } finally {
      setBusy(false);
      setPending(null);
    }
  }

  const lifecycleConf = org
    ? (LIFECYCLE_CONFIG[org.lifecycleStatus] ?? {
        label: org.lifecycleStatus,
        tone: 'default' as const,
      })
    : null;

  const commercialStatusConf = org
    ? (COMMERCIAL_STATUS_CONFIG[org.commercial.status] ?? {
        label: org.commercial.status,
        tone: 'default' as const,
      })
    : null;

  const commercialModeConf = org
    ? (COMMERCIAL_MODE_CONFIG[org.commercial.mode] ?? {
        label: org.commercial.mode,
        tone: 'default' as const,
      })
    : null;

  return (
    <OperatorPage
      title={org?.name ?? 'سازمان'}
      {...(org ? { leading: <OrgAvatar slug={org.slug} name={org.name} size={44} /> } : {})}
      actions={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {org ? (
            <Button
              type="button"
              variant="primary"
              disabled={supportLoginBusy}
              onClick={handleSupportLogin}
            >
              {supportLoginBusy ? 'در حال ورود...' : 'ورود با دسترسی پشتیبان به پنل تننت 🔐'}
            </Button>
          ) : null}
          {org ? (
            <a
              href={`/t/${org.slug}/login`}
              target="_blank"
              rel="noopener noreferrer"
              className="sak-btn sak-btn--secondary"
              style={{ textDecoration: 'none' }}
            >
              ورود به فضای سازمان ↗
            </a>
          ) : null}
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate('/internal/admin/organizations')}
          >
            بازگشت به فهرست
          </Button>
        </div>
      }
    >
      {supportLoginError ? (
        <Alert tone="danger" title="خطای ورود پشتیبان">
          {supportLoginError}
        </Alert>
      ) : null}
      {error ? (
        <Alert tone="danger" title="خطا">
          {error}
        </Alert>
      ) : null}

      {org ? (
        <>
          <Card title="اطلاعات هویتی و وضعیت سازمان">
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 12,
                margin: 0,
                alignItems: 'center',
              }}
            >
              <dt>نامک (آدرس اختصاصی)</dt>
              <dd>
                <code
                  dir="ltr"
                  style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: 'var(--sk-surface-inset, #f3f4f6)',
                    fontSize: 13,
                  }}
                >
                  /t/{org.slug}
                </code>
              </dd>

              <dt>وضعیت چرخه عمر</dt>
              <dd>
                {lifecycleConf ? (
                  <Badge tone={lifecycleConf.tone}>{lifecycleConf.label}</Badge>
                ) : (
                  org.lifecycleStatus
                )}
              </dd>

              <dt>شناسه مالک</dt>
              <dd dir="ltr" style={{ textAlign: 'right' }}>
                {org.ownerLoginIdentifier}
              </dd>

              <dt>طرح جاری</dt>
              <dd>{org.commercial.planDisplayName}</dd>

              <dt>حالت و وضعیت اشتراک</dt>
              <dd style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {commercialModeConf ? (
                  <Badge tone={commercialModeConf.tone}>{commercialModeConf.label}</Badge>
                ) : (
                  org.commercial.mode
                )}
                {commercialStatusConf ? (
                  <Badge tone={commercialStatusConf.tone}>{commercialStatusConf.label}</Badge>
                ) : (
                  org.commercial.status
                )}
              </dd>

              <dt>پایان مهلت آزمایشی</dt>
              <dd>
                {org.commercial.trialEndsAt
                  ? displayJalaliDate(org.commercial.trialEndsAt)
                  : 'بدون تاریخ پایان / غیرفعال'}
              </dd>

              <dt>صندلی‌های کاربر</dt>
              <dd dir="ltr" style={{ textAlign: 'right' }}>
                {org.commercial.seatUsed} از {org.commercial.seatLimit}
              </dd>

              <dt>تاریخ ایجاد</dt>
              <dd>{org.createdAt ? displayJalaliDate(org.createdAt) : '—'}</dd>
            </dl>
          </Card>

          <Card title="عملیات مدیریتی و تغییر وضعیت">
            <FormField label="دلیل عملیات (الزامی برای ممیزی و تاریخچه)" required>
              <Input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="دلیل تغییر وضعیت یا تنظیم دوره آزمایشی را وارد کنید..."
              />
            </FormField>

            <FormField
              label="تعداد روز تغییر دوره آزمایشی"
              hint="برای «تمدید» یا «کوتاه کردن» الزامی است (بین ۱ تا ۳۶۵ روز)؛ برای «لغو آزمایشی» نادیده گرفته می‌شود."
            >
              <Input
                type="number"
                min={MIN_TRIAL_ADJUSTMENT_DAYS}
                max={MAX_TRIAL_ADJUSTMENT_DAYS}
                value={days}
                onChange={(event) => setDays(event.target.value)}
              />
            </FormField>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
              {org.lifecycleStatus === 'suspended' ? (
                <Button
                  type="button"
                  onClick={() => setPending('reactivate')}
                  disabled={!reason.trim()}
                >
                  فعال‌سازی مجدد سازمان
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => setPending('suspend')}
                  disabled={!reason.trim()}
                >
                  تعلیق سازمان
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPending('extend')}
                disabled={!reason.trim() || !isValidTrialAdjustmentDays(days)}
              >
                تمدید آزمایشی (+{days} روز)
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPending('shorten')}
                disabled={!reason.trim() || !isValidTrialAdjustmentDays(days)}
              >
                کوتاه کردن آزمایشی (-{days} روز)
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => setPending('cancel')}
                disabled={!reason.trim()}
              >
                لغو دوره آزمایشی
              </Button>
            </div>
          </Card>

          {/* گزارش پیامک‌های ارسالی به این سازمان */}
          <Card title="تاریخچه پیامک‌های ارسالی به سازمان (SMS Deliveries)">
            {smsRefreshError ? <Alert tone="danger">{smsRefreshError}</Alert> : null}
            <Alert tone="info">
              «پذیرش سرویس‌دهنده» فقط دریافت درخواست توسط SMS.ir را نشان می‌دهد. وضعیت تحویل با
              استعلام جداگانه بررسی می‌شود و این عملیات هیچ پیامک جدیدی ارسال نمی‌کند.
            </Alert>
            {smsDeliveries === null ? (
              <LoadingState />
            ) : smsDeliveries.length === 0 ? (
              <EmptyState title="هیچ پیامکی برای این سازمان ثبت نشده است." />
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'mobile',
                    header: 'شماره گیرنده',
                    render: (row) => (
                      <span dir="ltr" style={{ fontFamily: 'monospace' }}>
                        {row.recipientMobile}
                      </span>
                    ),
                  },
                  {
                    key: 'template',
                    header: 'الگوی پیامک',
                    render: (row) => (
                      <span dir="ltr" style={{ fontSize: 13 }}>
                        {row.templateKey}
                      </span>
                    ),
                  },
                  {
                    key: 'providerMessageId',
                    header: 'شناسه سرویس‌دهنده',
                    render: (row) =>
                      row.providerMessageId ? <code>{row.providerMessageId}</code> : '—',
                  },
                  {
                    key: 'status',
                    header: 'پذیرش سرویس‌دهنده',
                    render: (row) => {
                      const accepted =
                        row.acceptanceStatus === 'accepted' || row.acceptanceStatus === 'sent';
                      return (
                        <Badge tone={accepted ? 'success' : 'danger'}>
                          {accepted ? 'پذیرفته‌شده' : row.acceptanceStatus}
                        </Badge>
                      );
                    },
                  },
                  {
                    key: 'deliveryState',
                    header: 'تحویل به گیرنده',
                    render: (row) => {
                      if (row.deliveryState === null)
                        return <Badge tone="default">بررسی‌نشده</Badge>;
                      const state = SMS_DELIVERY_STATE_CONFIG[row.deliveryState];
                      return state ? (
                        <Badge tone={state.tone}>{state.label}</Badge>
                      ) : (
                        <Badge tone="default">نامشخص</Badge>
                      );
                    },
                  },
                  {
                    key: 'time',
                    header: 'زمان ارسال',
                    render: (row) =>
                      row.sentAt ? displayJalaliDate(row.sentAt) : displayJalaliDate(row.createdAt),
                  },
                  {
                    key: 'checkedAt',
                    header: 'آخرین استعلام',
                    render: (row) =>
                      row.lastCheckedAt ? displayJalaliDateTime(row.lastCheckedAt) : '—',
                  },
                  {
                    key: 'error',
                    header: 'کد خطا',
                    render: (row) => {
                      const code = row.receiptErrorCode ?? row.errorCode;
                      return code ? <code>{code}</code> : '—';
                    },
                  },
                  {
                    key: 'refresh',
                    header: 'عملیات',
                    render: (row) => (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={
                          row.providerMessageId === null ||
                          refreshingDeliveryRef === row.deliveryRef
                        }
                        onClick={() => void handleSmsDeliveryRefresh(row.deliveryRef)}
                      >
                        {refreshingDeliveryRef === row.deliveryRef
                          ? 'در حال استعلام…'
                          : 'استعلام وضعیت تحویل'}
                      </Button>
                    ),
                  },
                ]}
                rows={[...smsDeliveries]}
                rowKey={(row) => row.deliveryRef}
              />
            )}
          </Card>

          {/* خریدها و ورود به درگاه این سازمان */}
          <Card title="خریدها و ورود به درگاه پرداخت (Orders & Payment Gateway)">
            {billingOrders === null ? (
              <LoadingState />
            ) : billingOrders.length === 0 ? (
              <EmptyState
                title="سفارش خریدی برای این سازمان ثبت نشده است."
                description="هیچ تلاش برای پرداخت یا ورود به درگاه بانکی برای این تننت وجود ندارد."
              />
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'date',
                    header: 'زمان و تاریخ',
                    render: (row) => (
                      <span style={{ fontSize: 13 }}>{displayJalaliDateTime(row.createdAt)}</span>
                    ),
                  },
                  {
                    key: 'buyer',
                    header: 'کاربر خریدار',
                    render: (row) => (
                      <div>
                        <div style={{ fontWeight: 600 }}>{row.ownerName}</div>
                        <div
                          dir="ltr"
                          style={{
                            fontSize: 12,
                            fontFamily: 'monospace',
                            color: 'var(--sk-color-text-secondary, #6b7280)',
                          }}
                        >
                          {row.ownerContactLabel}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'details',
                    header: 'طرح و مشخصات',
                    render: (row) => (
                      <span>
                        {row.planName || 'طرح استاندارد'} ({row.usersCount} کاربر •{' '}
                        {row.durationMonths} ماهه)
                      </span>
                    ),
                  },
                  {
                    key: 'amount',
                    header: 'مبلغ پرداختی',
                    render: (row) => (
                      <div>
                        <div style={{ fontWeight: 700 }}>
                          {Number(row.amountToman).toLocaleString('fa-IR')} تومان
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--sk-color-text-secondary, #6b7280)',
                          }}
                        >
                          {Number(row.amountRial).toLocaleString('fa-IR')} ریال
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: 'status',
                    header: 'وضعیت درگاه',
                    render: (row) => {
                      if (row.status === 'paid' || row.status === 'provisioned') {
                        return <Badge tone="success">پرداخت موفق</Badge>;
                      }
                      if (row.status === 'pending') {
                        return <Badge tone="warning">ورود به درگاه</Badge>;
                      }
                      return <Badge tone="danger">ناموفق / انصراف</Badge>;
                    },
                  },
                  {
                    key: 'gateway',
                    header: 'مشخصات بانکی',
                    render: (row) => (
                      <div dir="ltr" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {row.refId ? (
                          <div>
                            <strong>Ref:</strong> {row.refId}
                          </div>
                        ) : null}
                        {row.cardPan ? (
                          <div>
                            <strong>کارت:</strong> {row.cardPan}
                          </div>
                        ) : null}
                        {row.authority ? (
                          <div
                            title={row.authority}
                            style={{ color: 'var(--sk-color-text-secondary, #6b7280)' }}
                          >
                            Auth: {row.authority.slice(0, 14)}...
                          </div>
                        ) : null}
                        {row.failureReason ? (
                          <div
                            dir="rtl"
                            style={{ color: '#ef4444', fontFamily: 'inherit', marginTop: 2 }}
                          >
                            {row.failureReason}
                          </div>
                        ) : null}
                        {!row.refId && !row.authority && !row.failureReason ? <span>—</span> : null}
                      </div>
                    ),
                  },
                ]}
                rows={[...billingOrders]}
                rowKey={(row) => row.orderId}
              />
            )}
          </Card>

          {/* اعطای پنل و تغییر طرح اشتراک */}
          <Card title="اعطای پنل و تغییر طرح اشتراک به تننت">
            {changePlanSuccess ? (
              <Alert tone="success" title="موفقیت">
                {changePlanSuccess}
              </Alert>
            ) : null}
            {changePlanError ? (
              <Alert tone="danger" title="خطا">
                {changePlanError}
              </Alert>
            ) : null}
            <form
              onSubmit={handleChangePlan}
              style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: 14,
                }}
              >
                <FormField label="انتخاب طرح اشتراک مورد نظر" required>
                  <Select
                    value={changePlanRef}
                    onChange={(event) => setChangePlanRef(event.target.value)}
                  >
                    <option value="">— انتخاب طرح دلخواه —</option>
                    {(plans ?? []).map((p) => (
                      <option key={p.planRef} value={p.planRef}>
                        {`${p.displayName} (${
                          p.billingInterval === 'monthly'
                            ? 'ماهانـه'
                            : p.billingInterval === 'annual'
                              ? 'سـالانه'
                              : 'سفارشی'
                        } - ${Number(p.basePriceRial).toLocaleString('fa-IR')} ریال)`}
                      </option>
                    ))}
                  </Select>
                </FormField>

                <FormField
                  label="سقف صندلی اختصاصی (Seat Limit)"
                  hint="در صورت خالی گذاشتن، سقف پیش‌فرض صندلی‌های طرح انتخاب‌شده اعمال می‌شود."
                >
                  <Input
                    type="number"
                    min="1"
                    value={changeSeatLimit}
                    onChange={(event) => setChangeSeatLimit(event.target.value)}
                    placeholder={org ? String(org.commercial.seatLimit) : 'مثلاً: ۱۰'}
                  />
                </FormField>
              </div>

              <FormField label="دلیل اعطای یا تغییر طرح (الزامی جهت ممیزی و تاریخچه)" required>
                <Input
                  value={changePlanReason}
                  onChange={(event) => setChangePlanReason(event.target.value)}
                  placeholder="دلیل تغییر طرح را وارد نمایید (مثال: درخواست ارتقا توسط مدیریت، توافق قرارداد سازمانی)..."
                />
              </FormField>

              <div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={changePlanBusy || !changePlanRef || !changePlanReason.trim()}
                >
                  {changePlanBusy ? 'در حال اعمال طرح...' : 'اعطای طرح و به‌روزرسانی مشخصات تننت'}
                </Button>
              </div>
            </form>
          </Card>

          {/* کاربران سازمان و شماره‌های تماس */}
          <Card title={`کاربران و شماره‌های تماس سازمان (${users?.length ?? 0} کاربر)`}>
            {users === null ? (
              <LoadingState />
            ) : users.length === 0 ? (
              <EmptyState title="هیچ کاربری برای این تننت یافت نشد." />
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'name',
                    header: 'نام و نام خانوادگی',
                    render: (row) => (
                      <WithAvatar
                        avatar={
                          <OrgUserAvatar
                            slug={org?.slug ?? organizationRef ?? ''}
                            accountId={row.accountId}
                            name={row.displayName}
                            preset={row.avatarPreset}
                            hasPhoto={row.hasAvatarPhoto}
                          />
                        }
                      >
                        <strong>{row.displayName}</strong>
                      </WithAvatar>
                    ),
                  },
                  {
                    key: 'color',
                    header: 'رنگ انتخابی',
                    render: (row) => <BrandColorChip color={row.brandColor} />,
                  },
                  {
                    key: 'birthdate',
                    header: 'تاریخ تولد',
                    render: (row) => (
                      <span style={{ fontSize: 13 }}>{displayJalaliDate(row.birthdate)}</span>
                    ),
                  },
                  {
                    key: 'contact',
                    header: 'شماره تماس / موبایل',
                    render: (row) => (
                      <span
                        dir="ltr"
                        style={{
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          fontSize: 13,
                          background: 'var(--sk-surface-inset, #f3f4f6)',
                          padding: '3px 8px',
                          borderRadius: 4,
                          display: 'inline-block',
                        }}
                      >
                        {row.contactIdentifier || '—'}
                      </span>
                    ),
                  },
                  {
                    key: 'role',
                    header: 'نقش کاربر',
                    render: (row) => <Badge tone="info">{row.roleTitle}</Badge>,
                  },
                  {
                    key: 'status',
                    header: 'وضعیت حساب',
                    render: (row) => (
                      <Badge
                        tone={
                          row.status === 'active'
                            ? 'success'
                            : row.status === 'invited'
                              ? 'warning'
                              : 'danger'
                        }
                      >
                        {row.status === 'active'
                          ? 'فعال'
                          : row.status === 'invited'
                            ? 'دعوت‌شده'
                            : row.status}
                      </Badge>
                    ),
                  },
                  {
                    key: 'created',
                    header: 'تاریخ ایجاد / عضویت',
                    render: (row) => displayJalaliDate(row.createdAt),
                  },
                ]}
                rows={[...users]}
                rowKey={(row) => row.accountId}
              />
            )}
          </Card>

          {/* خطاها و هشدارهای تننت */}
          <Card title={`خطاها و هشدارهای تننت (${issues?.length ?? 0} مورد)`}>
            {issues === null ? (
              <LoadingState />
            ) : issues.length === 0 ? (
              <EmptyState title="هیچ خطایی برای این تننت ثبت نشده است (سیستم در وضعیت پایدار قرار دارد)." />
            ) : (
              <DataTable
                columns={[
                  {
                    key: 'category',
                    header: 'دسته‌بندی',
                    render: (row) => {
                      const catMap: Record<string, string> = {
                        sms: 'پیامک',
                        lifecycle: 'چرخه عمر',
                        operator: 'عملیات اپراتور',
                        audit: 'ممیزی',
                      };
                      return <Badge tone="default">{catMap[row.category] ?? row.category}</Badge>;
                    },
                  },
                  {
                    key: 'level',
                    header: 'سطح',
                    render: (row) => (
                      <Badge
                        tone={
                          row.level === 'error'
                            ? 'danger'
                            : row.level === 'warning'
                              ? 'warning'
                              : 'info'
                        }
                      >
                        {row.level === 'error'
                          ? 'خطا'
                          : row.level === 'warning'
                            ? 'هشدار'
                            : 'اطلاع'}
                      </Badge>
                    ),
                  },
                  {
                    key: 'title',
                    header: 'عنوان خطا',
                    render: (row) => <strong>{row.title}</strong>,
                  },
                  {
                    key: 'detail',
                    header: 'جزییات',
                    render: (row) => (
                      <span dir="ltr" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {row.detail}
                      </span>
                    ),
                  },
                  {
                    key: 'time',
                    header: 'زمان رخداد',
                    render: (row) => displayJalaliDate(row.occurredAt),
                  },
                ]}
                rows={[...issues]}
                rowKey={(row) => row.id}
              />
            )}
          </Card>
        </>
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title="تأیید عملیات حساس روی سازمان"
        message={`آیا از انجام عملیات «${
          pending === 'suspend'
            ? 'تعلیق سازمان'
            : pending === 'reactivate'
              ? 'فعال‌سازی مجدد'
              : pending === 'extend'
                ? `تمدید آزمایشی به مدت ${days} روز`
                : pending === 'shorten'
                  ? `کوتاه کردن آزمایشی به مدت ${days} روز`
                  : 'لغو دوره آزمایشی'
        }» اطمینان دارید؟`}
        danger={pending === 'suspend' || pending === 'cancel'}
        loading={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (pending) void run(pending);
        }}
      />
    </OperatorPage>
  );
}

export function CreateOrganizationPage() {
  const navigate = useNavigate();
  const [organizationName, setOrganizationName] = useState('');
  const [slug, setSlug] = useState('');
  const [ownerLoginIdentifier, setOwnerLoginIdentifier] = useState('');
  const [initialCredential, setInitialCredential] = useState('');
  const [planRef, setPlanRef] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [plans, setPlans] = useState<readonly OperatorPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    void listOperatorPlans().then((rows) => {
      const trialPlans = rows.filter((row) => row.active && row.trialEnabled);
      setPlans(trialPlans);
      if (trialPlans[0]) setPlanRef(trialPlans[0].planRef);
    });
  }, []);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const created = await createOperatorOrganization({
        organizationName,
        slug,
        ownerLoginIdentifier,
        initialCredential,
        planRef,
        mfaRequired,
      });
      navigate(`/internal/admin/organizations/${created.slug}`);
    } catch {
      setError('ساخت سازمان ناموفق بود. نامک تکراری یا مقادیر وارد شده نامعتبر است.');
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setConfirm(true);
  }

  return (
    <OperatorPage title="ساخت سازمان جدید">
      <Card>
        <form onSubmit={onSubmit} style={{ display: 'grid', gap: 16, maxWidth: 520 }}>
          <FormField label="نام سازمان" required>
            <Input
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              placeholder="مثلاً: شرکت نمونه آریا"
              required
            />
          </FormField>

          <FormField
            label="نامک / شناسه اینترنتی سازمان"
            required
            hint="حروف کوچک لاتین، رقم و خط تیره؛ حداکثر ۴۰ نویسه — بدون خط تیره در ابتدا یا انتها."
          >
            <Input
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              required
              pattern="[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?"
              maxLength={40}
              placeholder="مثلاً: arya-co"
              dir="ltr"
            />
          </FormField>

          <FormField label="شماره موبایل یا شناسه ورود مالک" required>
            <Input
              value={ownerLoginIdentifier}
              onChange={(event) => setOwnerLoginIdentifier(event.target.value)}
              placeholder="09123456789"
              required
              dir="ltr"
            />
          </FormField>

          <FormField label="رمز عبور اولیه مالک" required hint="حداقل ۱۲ نویسه امن.">
            <Input
              type="password"
              value={initialCredential}
              onChange={(event) => setInitialCredential(event.target.value)}
              placeholder="••••••••••••"
              required
              minLength={12}
            />
          </FormField>

          <FormField label="طرح اشتراک پایه" required>
            <Select value={planRef} onChange={(event) => setPlanRef(event.target.value)} required>
              {plans.map((plan) => {
                const rial = BigInt(plan.basePriceRial || '0');
                const toman = rial / 10n;
                const priceText =
                  toman === 0n ? 'رایگان' : `${toman.toLocaleString('fa-IR')} تومان`;
                const interval = plan.billingInterval === 'annual' ? 'سالانه' : 'ماهانه';
                return (
                  <option key={plan.planRef} value={plan.planRef}>
                    {plan.displayName} ({interval} — {priceText})
                  </option>
                );
              })}
            </Select>
          </FormField>

          <FormField label="امنیت و ورود">
            <Checkbox
              id="org-mfa-checkbox"
              label="الزام به احراز هویت دومرحله‌ای (MFA) برای این سازمان"
              checked={mfaRequired}
              onChange={(event) => setMfaRequired(event.target.checked)}
            />
          </FormField>

          {error ? (
            <Alert tone="danger" title="خطا در ایجاد سازمان">
              {error}
            </Alert>
          ) : null}

          <Button type="submit">ایجاد و راه‌اندازی سازمان</Button>
        </form>
      </Card>

      <ConfirmDialog
        open={confirm}
        title="ایجاد سازمان واقعی"
        message="یک tenant واقعی به همراه زیرساخت داده و دوره آزمایشی راه‌اندازی می‌شود. ادامه می‌دهید؟"
        loading={busy}
        onCancel={() => setConfirm(false)}
        onConfirm={() => void submit()}
      />
    </OperatorPage>
  );
}
